import { ReactNode } from 'react';

import {
  CarOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  FileDoneOutlined,
} from '@ant-design/icons';
import { Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import { TiendaPerfectaRouteReportKpis } from 'models/TiendaPerfecta';

import { percentOfScheduled } from '../utils/kpis';
import AnimatedNumber from './AnimatedNumber';

const I18N_BASE = 'scenes.route_report.components.kpis';

interface KpiCardProps {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  hint: string;
  variant?: 'default' | 'alert' | 'good';
}

function KpiCard({
  icon,
  label,
  value,
  hint,
  variant = 'default',
}: KpiCardProps) {
  return (
    <div className={`route-report-kpi route-report-kpi--${variant}`}>
      <div className="route-report-kpi__top">
        {icon}
        {label}
      </div>
      <div className="route-report-kpi__value">{value}</div>
      <div className="route-report-kpi__hint">{hint}</div>
    </div>
  );
}

interface RouteReportKpisProps {
  kpis: TiendaPerfectaRouteReportKpis | null;
  loading?: boolean;
}

/**
 * Los cinco contadores del día.
 *
 * Camionetas (no rutas): `vansOnRoute` y `vansNotStarted`. Los tres campos
 * nuevos (`vansOnRoute`, `vansNotStarted`, `successfulStops`) son opcionales
 * en el BFF: ausentes se pintan «—», nunca 0, que afirmaría un dato que no
 * existe. `unitsOnRoute` / `unitsNotStarted` ya no se leen acá.
 */
function RouteReportKpis({ kpis, loading = false }: RouteReportKpisProps) {
  const { t } = useTranslation();

  const value = (n: number | undefined) =>
    loading || kpis == null ? (
      <Skeleton.Input active style={{ width: 58, height: 38 }} />
    ) : n == null ? (
      '—'
    ) : (
      <AnimatedNumber value={n} showDelta />
    );

  // «—» mientras carga (aunque queden kpis previos: su porcentaje ya no
  // vale), sin kpis o si el valor falta: en los tres casos no hay base.
  const percent = (n: number | undefined) => {
    const result =
      loading || kpis == null
        ? null
        : percentOfScheduled(n, kpis.scheduledStops);

    return result == null ? '—' : `${result}%`;
  };

  return (
    <div className="route-report-kpis">
      <KpiCard
        icon={<CarOutlined />}
        // Las claves i18n conservan el nombre viejo `units_*`, pero el valor
        // sale de `vansOnRoute` / `vansNotStarted` (camionetas, no rutas).
        label={t(`${I18N_BASE}.units_on_route.label`)}
        value={value(kpis?.vansOnRoute)}
        hint={t(`${I18N_BASE}.units_on_route.hint`)}
      />
      <KpiCard
        icon={<ExclamationCircleOutlined />}
        label={t(`${I18N_BASE}.units_not_started.label`)}
        value={value(kpis?.vansNotStarted)}
        hint={t(`${I18N_BASE}.units_not_started.hint`)}
        // Solo se destaca cuando hay algo que atender.
        variant={
          !loading && (kpis?.vansNotStarted ?? 0) > 0 ? 'alert' : 'default'
        }
      />
      <KpiCard
        icon={<EnvironmentOutlined />}
        label={t(`${I18N_BASE}.scheduled_stops.label`)}
        value={value(kpis?.scheduledStops)}
        hint={t(`${I18N_BASE}.scheduled_stops.hint`)}
      />
      <KpiCard
        icon={<FileDoneOutlined />}
        label={t(`${I18N_BASE}.visited_stops.label`)}
        value={value(kpis?.completedStops)}
        hint={t(`${I18N_BASE}.visited_stops.hint`, {
          percent: percent(kpis?.completedStops),
        })}
      />
      <KpiCard
        icon={
          <CheckCircleOutlined className="route-report-kpi__icon--success" />
        }
        label={t(`${I18N_BASE}.successful_stops.label`)}
        value={value(kpis?.successfulStops)}
        hint={t(`${I18N_BASE}.successful_stops.hint`, {
          percent: percent(kpis?.successfulStops),
        })}
        variant="good"
      />
    </div>
  );
}

export default RouteReportKpis;
